/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import type * as Type from '@/common/interface';

export const downloadPlatformLabel: Record<string, string> = {
  windows: 'Windows',
  macos: 'macOS',
  cross_platform: 'Windows / macOS',
  android: 'Android APK',
  ios: 'iOS / TestFlight',
};

export const downloadPlatformIconNames: Record<string, string[]> = {
  windows: ['pc-display-horizontal'],
  macos: ['apple'],
  cross_platform: ['pc-display-horizontal', 'apple'],
  android: ['phone'],
  ios: ['apple'],
};

export const getDownloadPlatformIconNames = (platform: string) =>
  downloadPlatformIconNames[platform] || ['box-seam'];

export const getSoftwareDisplayName = (
  item: Type.PublicDownloadRecommendationItem,
) => {
  const identity = [item.software_name, item.package_original_name]
    .filter(Boolean)
    .join(' ');
  if (
    item.platform === 'android' &&
    /(?:codex.*(?:mobile|移动端)|(?:mobile|移动端).*codex)/i.test(identity)
  ) {
    return 'Codex-安卓主控端';
  }
  return item.software_name;
};

export const getSoftwareDescription = (
  item: Type.PublicDownloadRecommendationItem,
) =>
  item.recommend_reason ||
  item.package_release_note ||
  '本站已发布的软件安装包。';

export const formatSoftwareFileSize = (size: number) => {
  if (!size) {
    return '未知大小';
  }
  if (size >= 1024 * 1024 * 1024) {
    return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`;
  }
  if (size >= 1024 * 1024) {
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

export const formatSoftwareUpdatedAt = (
  item: Type.PublicDownloadRecommendationItem,
) => {
  const timestamp = item.updated_at || item.published_at;
  if (!timestamp) {
    return '未记录';
  }
  return new Date(timestamp * 1000).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export const formatSoftwareDownloadCount = (count: number) =>
  `${count || 0} 次`;

export const escapeSoftwareCardHTML = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const buildSoftwareCardHTML = (
  item: Type.PublicDownloadRecommendationItem,
) => {
  const name = escapeSoftwareCardHTML(getSoftwareDisplayName(item));
  const description = escapeSoftwareCardHTML(getSoftwareDescription(item));
  const version = escapeSoftwareCardHTML(item.package_version || 'Latest');
  const platform = escapeSoftwareCardHTML(
    downloadPlatformLabel[item.platform] || item.platform,
  );
  const size = escapeSoftwareCardHTML(
    formatSoftwareFileSize(item.package_size),
  );
  const updatedAt = escapeSoftwareCardHTML(formatSoftwareUpdatedAt(item));
  const downloadCount = escapeSoftwareCardHTML(
    formatSoftwareDownloadCount(item.download_count),
  );

  return `<div class="software-info-card">
  <div class="software-info-card-title">${name}</div>
  <p class="software-info-card-desc">${description}</p>
  <div class="software-info-card-meta">${platform} · ${version} · ${size} · 更新 ${updatedAt} · 下载 ${downloadCount}</div>
  <a class="software-info-card-link" href="/downloads#download-catalog" data-download-id="${item.id}">本站软件下载</a>
</div>`;
};

export const isCodexPackage = (item: Type.PublicDownloadRecommendationItem) =>
  /codex/i.test(item.software_name) ||
  /codex/i.test(item.package_original_name || '');

export const isFullInstallerPackage = (
  item: Type.PublicDownloadRecommendationItem,
) =>
  /(离线|全量|完整|安装包|full|offline|x64)/i.test(
    [
      item.software_name,
      item.package_original_name,
      item.recommend_reason,
      item.package_release_note,
    ].join(' '),
  );
